import {
  ArrowDownward,
  ArrowUpward,
  ContentCopy,
  Delete,
  DragIndicator,
  Edit,
  Visibility,
} from '@mui/icons-material';
import { IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { LayoutItem } from 'api/models';
import { useTranslation } from 'react-i18next';
import MuiIcon from 'components/MuiIcon/MuiIcon';
import { getFieldIcon } from './AddFieldButton';
import { LayoutItemSubType, LayoutItemType } from 'api/models/FormTemplate';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  confirmDialogState,
  dialogState,
  fieldByIdSelector,
  fieldTemplatesState,
  layoutSelector,
} from 'state';
import { DialogTypes } from 'types/types';
import { useCopyLayoutItem } from 'hooks/useCopyLayoutItem';
import { LoadingButton } from '@mui/lab';
import { dragState } from 'state/dragState';
import { useDrag } from 'react-dnd';
import { memo, useEffect } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import DragPreview from './DragPreview';
import { produce } from 'immer';
import { get, del } from 'object-path';
import { DropParams } from './DropArea';
import { getFieldIdsOfLayoutField } from '../utils';
import useDragScrolling from 'hooks/useDragScrolling';

const iconStyles = {
  width: 15,
  height: 15,
};
interface FieldTemplateControlsProps {
  layoutItem: LayoutItem;
  disabled?: boolean;
  fieldId: string;
  parentPath: string;
  index: number;
}

function FieldTemplateControls({
  layoutItem,
  disabled,
  fieldId,
  parentPath,
  index,
}: FieldTemplateControlsProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const openDialog = useSetRecoilState(dialogState);
  const { loading, setClipboard } = useCopyLayoutItem();
  const fieldTemplate = useRecoilValue(fieldByIdSelector(fieldId));
  const setDraggedItem = useSetRecoilState(dragState);
  const setDialogStore = useSetRecoilState(confirmDialogState);
  const [fieldTemplates, setFieldTemplates] =
    useRecoilState(fieldTemplatesState);
  const [layout, setLayout] = useRecoilState(layoutSelector);
  const { addEventListenerForWindow, removeEventListenerForWindow } = useDragScrolling();
  const [{ isDragging }, dragRef, dragPreview] = useDrag(() => {
    const item: DropParams = {
      layoutItem,
      fromPath: `${parentPath}.${index}`,
      field: fieldTemplate,
    };
    return {
      end: () => {
        removeEventListenerForWindow();
      },
      type: 'field-template',
      item: () => {
        addEventListenerForWindow();
        return item;
      },
      canDrag: () => !disabled,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
      previewOptions: {
        captureDraggingState: false,
      },
    };
  }, [parentPath, index, layoutItem]);

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: false });
  }, [dragPreview]);

  useEffect(() => {
    isDragging ? setDraggedItem(layoutItem) : setDraggedItem(undefined);
  }, [isDragging, layoutItem, setDraggedItem]);

  const onMove = (direction: 'up' | 'down') => {
    setLayout(
      produce(layout, draft => {
        const list = get(draft, parentPath);
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= list.length) {
          return;
        }
        [list[index], list[newIndex]] = [list[newIndex], list[index]];
      }),
    );
  };

  const onDeleteConfirm = (item: LayoutItem) => {
    setLayout(
      produce(layout, draft => {
        del(draft, `${parentPath}.${index}`);
      }),
    );
    const idsToBeDeleted = getFieldIdsOfLayoutField(item);
    setFieldTemplates(
      fieldTemplates.filter(f => !idsToBeDeleted.includes(f.id)),
    );
  };

  const onDelete = (field: LayoutItem) => {
    setDialogStore({
      isOpen: true,
      dialogType: DialogTypes.confirmDelete,
      action: () => onDeleteConfirm(field),
    });
  };

  const onEdit = (layoutItem: LayoutItem) => {
    switch (layoutItem.type) {
      case LayoutItemType.Container:
        openDialog({
          isOpen: true,
          dialogType:
            layoutItem.subType === LayoutItemSubType.SelectableTable
              ? DialogTypes.editSelectableTableField
              : DialogTypes.editLayoutItem,
          data: {
            layoutItem,
            path: `${parentPath}.${index}`,
          },
        });

        break;
      case LayoutItemType.Static:
        openDialog({
          isOpen: true,
          dialogType: DialogTypes.editHeaderItem,
          data: {
            layoutItem,
            path: `${parentPath}.${index}`,
          },
        });
        break;
      default:
        if (fieldTemplate) {
          openDialog({
            isOpen: true,
            dialogType: DialogTypes.addFieldDialog,
            data: { fieldTemplate, layoutItem, path: parentPath },
          });
        }
    }
  };

  const sx = {
    background:
      theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : theme.palette.grey[200],
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.grey[300]
        : theme.palette.grey[600],
    width: 25,
    height: 25,
    ml: 1,
    '&:hover': {
      background: theme.palette.action.hover,
    },
  };

  return (
    <Box display={'flex'} justifyContent={'end'} mb={1}>
      <DragPreview />
      <Box width="100%" flexDirection={'row'} display="flex">
        <Typography
          fontSize={10}
          fontWeight={'bold'}
          color={theme => theme.palette.text.disabled}
        >
          <MuiIcon
            icon={getFieldIcon(
              fieldTemplate?.fieldType ?? layoutItem.subType ?? layoutItem.type,
            )}
            sx={{ mr: 1, width: '20px', height: '20px' }}
          />
        </Typography>
        {
          <Typography
            fontSize={12}
            color={theme => theme.palette.text.primary}
            pt={0.2}
            sx={{
              color: theme => theme.palette.text.secondary,
            }}
          >
            {fieldTemplate?.alias ?? ''}
          </Typography>
        }
      </Box>
      {layoutItem.subType !== LayoutItemSubType.HorizontalSeparator && (
        <Tooltip title={disabled ? t('general.view') : t('general.edit')}>
          <IconButton size="small" onClick={() => onEdit(layoutItem)} sx={sx}>
            {disabled ? (
              <Visibility sx={iconStyles} />
            ) : (
              <Edit sx={iconStyles} />
            )}
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title={t('general.moveUp')}>
        <IconButton
          size="small"
          onClick={() => onMove('up')}
          sx={sx}
          disabled={disabled}
        >
          <ArrowUpward sx={iconStyles} />
        </IconButton>
      </Tooltip>

      <Tooltip title={t('general.moveDown')}>
        <IconButton
          size="small"
          onClick={() => onMove('down')}
          sx={sx}
          disabled={disabled}
        >
          <ArrowDownward sx={iconStyles} />
        </IconButton>
      </Tooltip>

      <Tooltip title={t('general.copy')}>
        <LoadingButton
          sx={{
            ...sx,
            minWidth: 25,
            width: 25,
            height: 25,
            borderRadius: '50%',
            backgroundColor: disabled
              ? 'transparent'
              : theme => theme.palette.background.default,
          }}
          loading={loading}
          disabled={disabled}
          size="small"
          onClick={() => setClipboard(layoutItem)}
        >
          <ContentCopy sx={iconStyles} />
        </LoadingButton>
      </Tooltip>

      <Tooltip title={t('general.delete')}>
        <IconButton
          size="small"
          onClick={() => onDelete(layoutItem)}
          sx={sx}
          disabled={disabled}
        >
          <Delete sx={iconStyles} />
        </IconButton>
      </Tooltip>

      <Tooltip title={t('general.drag')}>
        <IconButton size="small" sx={sx} disabled={disabled} ref={dragRef}>
          <DragIndicator
            sx={{
              ...iconStyles,
              cursor: 'move',
            }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default memo(FieldTemplateControls);
